import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useDispatch } from 'react-redux';
import {
  Button,
  ButtonGroup,
  colors,
  TextField,
  Typography
} from '@material-ui/core';
import {
  updateEventValidationStatus,
  updateEventAnnotatedParticipantGender,
  updateEventAnnotatedParticipantClothingType,
  toggleEventSpecialInterest
} from 'actions';
import { VALIDATION_EXCLUDED_REASONS } from 'common/constants';

const useStyles = makeStyles(theme => ({
  excludedReasonRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5)
  },
  excludedReasonLabel: {
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap'
  },
  excludedReasonSelect: {
    flexGrow: 1,
    margin: 0
  },
  attributeSelection: {
    '& .MuiSvgIcon-root': {
      width: '0.8em',
      height: '0.8em',
      marginLeft: theme.spacing(1)
    },
    '& .MuiButton-root': {
      fontSize: '0.8em',
      height: 28
    },
    marginBottom: theme.spacing(1)
  },
  selectedButton: {
    backgroundColor: colors.blueGrey[50],
    boxShadow: 'inset 0 0 4px #B0BEC5'
  }
}));

const EventAnnotations = props => {
  const {
    event,
    processing,
    onUpdateValidationStatus,
    hideGender,
    hideValidationStatus,
    hideClothingType,
    hideSpecialInterest,
    shortcutHints,
    className
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const handleUpdateEventValidationStatus = (status, validationExcludedReason) => {
    if (onUpdateValidationStatus) {
      onUpdateValidationStatus(status, validationExcludedReason);
      return;
    }
    dispatch(
      updateEventValidationStatus(event.id, status, validationExcludedReason)
    );
  };

  const handleExcludedReasonChanged = e => {
    const reason = e.target.value;
    e.target.blur();
    if (reason && reason !== event.validationExcludedReason) {
      handleUpdateEventValidationStatus('SUSPECT', reason);
    }
  };

  const handleUpdateEventParticipantGender = gender => {
    dispatch(
      updateEventAnnotatedParticipantGender(
        event.id,
        event.annotatedParticipantGender === gender ? null : gender
      )
    );
  };

  const handleUpdateEventParticipantClothingType = clothingType => {
    dispatch(
      updateEventAnnotatedParticipantClothingType(
        event.id,
        event.annotatedParticipantClothingType === clothingType
          ? null
          : clothingType
      )
    );
  };

  const handleToggleEventSpecialInterest = event => {
    dispatch(toggleEventSpecialInterest(event.id));
  };

  return (
    <div className={className}>
      {!hideValidationStatus && (
        <>
          <ButtonGroup className={classes.attributeSelection} fullWidth>
            <Button
              disabled={processing}
              onClick={() => handleUpdateEventValidationStatus('PENDING')}
              variant="outlined"
              className={
                event.validationStatus === 'PENDING' ? classes.selectedButton : ''
              }>
              {shortcutHints ? 'Pending (P)' : 'Pending'}
            </Button>
            <Button
              disabled={processing}
              onClick={() => {
                if (event.validationStatus !== 'SUSPECT') {
                  handleUpdateEventValidationStatus('SUSPECT', 'UNKNOWN');
                }
              }}
              variant="outlined"
              className={
                event.validationStatus === 'SUSPECT' ? classes.selectedButton : ''
              }>
              {shortcutHints ? 'Excluded (E)' : 'Excluded'}
            </Button>
            <Button
              disabled={processing}
              onClick={() => handleUpdateEventValidationStatus('VERIFIED')}
              variant="outlined"
              className={
                event.validationStatus === 'VERIFIED'
                  ? classes.selectedButton
                  : ''
              }>
              {shortcutHints ? 'Verified (V)' : 'Verified'}
            </Button>
          </ButtonGroup>
          {event.validationStatus === 'SUSPECT' && (
            <div className={classes.excludedReasonRow}>
              <Typography
                className={classes.excludedReasonLabel}
                color="textSecondary"
                variant="body2">
                Excluded reason
              </Typography>
              <TextField
                className={classes.excludedReasonSelect}
                disabled={processing}
                inputProps={{ 'aria-label': 'Excluded reason' }}
                margin="dense"
                onChange={handleExcludedReasonChanged}
                select
                SelectProps={{ native: true }}
                value={event.validationExcludedReason || ''}
                variant="outlined">
                <option disabled value="">
                  Select a reason
                </option>
                {Object.keys(VALIDATION_EXCLUDED_REASONS).map(key => (
                  <option key={key} value={key}>
                    {VALIDATION_EXCLUDED_REASONS[key]}
                  </option>
                ))}
              </TextField>
            </div>
          )}
        </>
      )}
      {!hideGender && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantGender('MALE')}
            variant="outlined"
            className={
              event.annotatedParticipantGender === 'MALE'
                ? classes.selectedButton
                : ''
            }>
            Male
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantGender('FEMALE')}
            variant="outlined"
            className={
              event.annotatedParticipantGender === 'FEMALE'
                ? classes.selectedButton
                : ''
            }>
            Female
          </Button>
        </ButtonGroup>
      )}
      {!hideClothingType && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('LIGHT')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'LIGHT'
                ? classes.selectedButton
                : ''
            )}>
            Light Clothing
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('MEDIUM')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'MEDIUM'
                ? classes.selectedButton
                : ''
            )}>
            Medium Clothing
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('HEAVY')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'HEAVY'
                ? classes.selectedButton
                : ''
            )}>
            Heavy Clothing
          </Button>
        </ButtonGroup>
      )}
      {!hideSpecialInterest && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleToggleEventSpecialInterest(event)}
            variant="outlined"
            className={event.specialInterest ? classes.selectedButton : ''}>
            Special Interest
          </Button>
        </ButtonGroup>
      )}
    </div>
  );
};

EventAnnotations.propTypes = {
  event: PropTypes.object,
  processing: PropTypes.bool,
  onUpdateValidationStatus: PropTypes.func,
  shortcutHints: PropTypes.bool
};

export default EventAnnotations;
